import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { Upload, Loader2, CheckCircle2, AlertTriangle, Copy, Check, FileJson } from "lucide-react";
import api, { API } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogDescription,
    DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

export default function ImportDialog({ open, onOpenChange, onIngested }) {
    const { t } = useI18n();
    const [file, setFile] = useState(null);
    const [uploading, setUploading] = useState(false);
    const [result, setResult] = useState(null);
    const [token, setToken] = useState("");
    const [copied, setCopied] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (open) {
            setFile(null);
            setResult(null);
            setCopied(false);
            api.get("/config/ingest-token")
                .then((r) => setToken(r.data.token || ""))
                .catch(() => setToken(""));
        }
    }, [open]);

    const copyToken = async () => {
        try {
            await navigator.clipboard.writeText(token);
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        } catch (e) {
            /* clipboard may be blocked; ignore */
        }
    };

    const doUpload = async () => {
        if (!file) return;
        let parsed;
        try {
            const text = await file.text();
            parsed = JSON.parse(text);
        } catch (e) {
            toast.error(t("invalid_json"));
            return;
        }
        if (!token) {
            toast.error(t("error_generic"));
            return;
        }
        setUploading(true);
        setResult(null);
        try {
            const r = await axios.post(`${API}/sales/ingest`, parsed, {
                headers: { "X-Ingest-Token": token, "Content-Type": "application/json" },
                timeout: 180000,
                maxContentLength: Infinity,
                maxBodyLength: Infinity,
            });
            setResult(r.data);
            toast.success(t("import_done"));
            onIngested && onIngested();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setUploading(false);
        }
    };

    const sumField = (key) =>
        result ? Object.values(result.counts || {}).reduce((a, c) => a + (c[key] || 0), 0) : 0;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid={OPS.importer.dialog} className="max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Upload className="h-5 w-5 text-[color:var(--brand-orange)]" />
                        {t("import_sales")}
                    </DialogTitle>
                    <DialogDescription>{t("import_desc")}</DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {/* File picker */}
                    <div
                        className="rounded-lg border border-dashed border-[color:var(--border-strong)] surface-2 p-4 flex items-center gap-3 cursor-pointer"
                        onClick={() => fileRef.current?.click()}
                    >
                        <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-[color:rgba(186,217,235,0.5)]">
                            <FileJson className="h-5 w-5 text-[color:var(--brand-brown)]" />
                        </span>
                        <div className="min-w-0">
                            <p className="text-sm font-medium text-[color:var(--ink)]">
                                {file ? `${t("selected_file")}: ${file.name}` : t("choose_file")}
                            </p>
                            <p className="text-xs text-[color:var(--ink-muted)]">JSON</p>
                        </div>
                        <input
                            ref={fileRef}
                            data-testid={OPS.importer.file}
                            type="file"
                            accept="application/json,.json"
                            className="hidden"
                            onChange={(e) => {
                                setFile(e.target.files?.[0] || null);
                                setResult(null);
                            }}
                        />
                    </div>

                    {/* Service token */}
                    <div className="space-y-1.5">
                        <Label className="text-xs text-[color:var(--ink-muted)]">{t("service_token")}</Label>
                        <div className="flex items-center gap-2">
                            <Input
                                data-testid={OPS.importer.token}
                                value={token}
                                readOnly
                                dir="ltr"
                                className="text-start font-mono text-xs surface-2"
                            />
                            <Button type="button" variant="outline" size="icon" onClick={copyToken} data-testid={OPS.importer.copy}>
                                {copied ? <Check className="h-4 w-4 text-[color:var(--success)]" /> : <Copy className="h-4 w-4" />}
                            </Button>
                        </div>
                        <p className="text-[11px] text-[color:var(--ink-muted)]">{t("token_hint")}</p>
                    </div>

                    {/* Result */}
                    {result ? (
                        <div data-testid={OPS.importer.result} className="rounded-lg surface-2 border border-[color:var(--border-soft)] p-3 space-y-2">
                            <div className="flex items-center gap-2 text-sm font-semibold text-[color:var(--success)]">
                                <CheckCircle2 className="h-4 w-4" />
                                {t("import_done")}
                            </div>
                            <div className="grid grid-cols-3 gap-2 text-center">
                                <div className="rounded surface p-2">
                                    <p className="text-lg font-bold tnum text-[color:var(--ink)]">{sumField("inserted")}</p>
                                    <p className="text-[11px] text-[color:var(--ink-muted)]">{t("rows_inserted")}</p>
                                </div>
                                <div className="rounded surface p-2">
                                    <p className="text-lg font-bold tnum text-[color:var(--ink)]">{sumField("updated")}</p>
                                    <p className="text-[11px] text-[color:var(--ink-muted)]">{t("rows_updated")}</p>
                                </div>
                                <div className="rounded surface p-2">
                                    <p className={`text-lg font-bold tnum ${result.rejected_count ? "text-[color:var(--danger)]" : "text-[color:var(--ink)]"}`}>
                                        {result.rejected_count}
                                    </p>
                                    <p className="text-[11px] text-[color:var(--ink-muted)]">{t("rejected_rows")}</p>
                                </div>
                            </div>
                            {result.rejected_count ? (
                                <div className="max-h-28 overflow-y-auto scrollbar-thin text-[11px] text-[color:var(--danger)] space-y-1">
                                    {result.rejected.slice(0, 20).map((r, i) => (
                                        <div key={i} className="flex items-center gap-1">
                                            <AlertTriangle className="h-3 w-3 shrink-0" />
                                            <span dir="ltr">{r.collection}[{r.row}]: {r.reason}</span>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <p className="text-[11px] text-[color:var(--ink-muted)]">{t("no_rejected")}</p>
                            )}
                        </div>
                    ) : null}
                </div>

                <DialogFooter className="gap-2">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={uploading}>
                        {t("cancel")}
                    </Button>
                    <Button
                        data-testid={OPS.importer.upload}
                        onClick={doUpload}
                        disabled={!file || uploading}
                        className="bg-[color:var(--brand-orange)] hover:bg-[color:var(--brand-orange-hover)] text-white"
                    >
                        {uploading ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : <Upload className="h-4 w-4 me-2" />}
                        {uploading ? t("uploading") : t("upload")}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
